import {
  Button,
  CircularLoader,
  ModalV2,
  Stack,
  Title,
  useActions,
  useToast
} from "@introist/react-foundation/v2";
import { Allotment } from "allotment";
import "allotment/dist/style.css";
import { api } from "../../../services/rpc/RpcProvider";
import { FunctionEditor } from "../../../components/templates/CodeEditor";
import { CodeEditor } from "../../../components/templates/CodeEditor/CodeEditor";
import { FormFooter } from "../../datasources/components/FormFooter";
import { useFeatureFlag } from "../../../hooks";
import { ChatMessage, ChatPane } from "../../../components/TransformBuilder/ChatPane";
import { TestCasesPane } from "../../../components/TransformBuilder/TestCasesPane";
import {
  parseTestCase,
  TestCase,
  TestCaseFormat,
  toTestCase,
  TransformTestResult
} from "../../../components/TransformBuilder/testcases";
import { useCallback, useEffect, useState } from "react";

type TransformModalProps = {
  open: boolean;
  onClose: () => void;
  fieldKey: string;
};

const AI_TRANSFORM_BUILDER_FLAG = "ai-transform-builder";

const format: TestCaseFormat = { args: ["employee"], optionalArgs: ["history"] };

type FieldTestCase = {
  id: string;
  name: string;
  employee: Record<string, unknown>;
  history?: Record<string, unknown> | null;
};

const byId = (results: TransformTestResult[]) => Object.fromEntries(results.map(r => [r.id, r]));

type AiBuilderProps = {
  fieldKey: string;
  value: string;
  onChange: (value: string) => void;
  onWorkingChange: (working: boolean) => void;
};

const AiTransformBuilder = ({ fieldKey, value, onChange, onWorkingChange }: AiBuilderProps) => {
  const toast = useToast();

  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [results, setResults] = useState<Record<string, TransformTestResult>>({});
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const runTests = api.employees.v4.fields.computed.testCases.useMutation();
  const assistant = api.employees.v4.fields.computed.assistant.useMutation();

  const working = assistant.isLoading;
  useEffect(() => onWorkingChange(working), [working, onWorkingChange]);

  const parseTestCases = useCallback(() => {
    try {
      // parseTestCase guarantees employee is an object and history an object or null
      return testCases.map(t => parseTestCase(format, t) as FieldTestCase);
    } catch (e: any) {
      toast.error(e.message);
      return undefined;
    }
  }, [testCases, toast]);

  const onRunTests = useCallback(async () => {
    const cases = parseTestCases();
    if (!cases) return;

    return runTests
      .mutateAsync({ code: value, cases })
      .then(res => setResults(byId(res)))
      .catch(() => toast.error("Failed to run tests"));
  }, [parseTestCases, runTests, value, toast]);

  const onSendMessage = useCallback(
    async (content: string) => {
      const cases = parseTestCases();
      if (!cases) return;

      const history: ChatMessage[] = [...messages, { role: "user", content }];
      setMessages(history);

      return assistant
        .mutateAsync({
          field: fieldKey,
          messages: history.flatMap(m => (m.role === "note" ? [] : [{ ...m, role: m.role }])),
          code: value,
          testCases: cases
        })
        .then(res => {
          const notes: ChatMessage[] = [];
          if (res.code !== undefined && res.code !== value) {
            onChange(res.code);
            notes.push({ role: "note", content: "Updated the code" });
          }
          if (res.newTestCases.length > 0) {
            setTestCases(current => [
              ...current,
              ...res.newTestCases.map(t => toTestCase(format, t))
            ]);
            const count = res.newTestCases.length;
            notes.push({
              role: "note",
              content: `Added ${count} test case${count === 1 ? "" : "s"}`
            });
          }
          if (res.results) setResults(byId(res.results));
          setMessages([...history, ...notes, { role: "assistant", content: res.reply }]);
        })
        .catch(() => {
          toast.error("The assistant failed to respond");
          setMessages(messages);
        });
    },
    [parseTestCases, messages, assistant, fieldKey, value, onChange, toast]
  );

  return (
    <div style={{ height: "65vh" }}>
      <Allotment separator={false}>
        <Allotment.Pane preferredSize="30%">
          <ChatPane
            messages={messages}
            onSend={onSendMessage}
            loading={working}
            intro={`Describe how the value should be computed, e.g. "six months after the start date, empty if there is no start date".`}
          />
        </Allotment.Pane>
        <Allotment.Pane>
          <Stack
            vertical
            gap="xSmall"
            style={{ padding: "var(--spacing-small)", boxSizing: "border-box", height: "100%" }}
          >
            <Title>Code</Title>
            <CodeEditor value={value} onChange={onChange} height="100%" readOnly={working} />
          </Stack>
        </Allotment.Pane>
        <Allotment.Pane>
          <TestCasesPane
            format={format}
            testCases={testCases}
            onChange={setTestCases}
            results={results}
            onRun={onRunTests}
            disabled={working}
          />
        </Allotment.Pane>
      </Allotment>
    </div>
  );
};

const TransformEditor = ({
  fieldKey,
  onClose,
  aiEnabled
}: TransformModalProps & { aiEnabled: boolean }) => {
  const { onConfirmAction } = useActions();
  const toast = useToast();

  const { data: computed } = api.employees.v4.fields.computed.find.useQuery(
    {
      field: fieldKey
    },
    {
      refetchOnMount: "always"
    }
  );

  const apiUtils = api.useContext();

  const update = api.employees.v4.fields.computed.update.useMutation();
  const remove = api.employees.v4.fields.computed.delete.useMutation();

  const [value, setValue] = useState<string>("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (computed && computed.code) setValue(computed.code);
  }, [computed]);

  const onRemove = onConfirmAction(
    async () => {
      await remove
        .mutateAsync({ field: fieldKey })
        .then(async () => {
          await apiUtils.employees.v4.fields.computed.list.refetch();
          toast.success("Field transformation removed");
          onClose();
        })
        .catch(() => toast.error("Failed to remove field transformation"));
    },
    {
      title: "Remove field transformation",
      description: "Are you sure you want to remove this field transformation?",
      confirmTitle: "Remove",
      cancelTitle: "Cancel"
    }
  );

  if (!computed)
    return (
      <div style={{ height: "600px" }}>
        <CircularLoader fillParent />
      </div>
    );

  return (
    <>
      <Stack vertical gap="large">
        {aiEnabled ? (
          <AiTransformBuilder
            fieldKey={fieldKey}
            value={value}
            onChange={setValue}
            onWorkingChange={setWorking}
          />
        ) : (
          <FunctionEditor value={value} onChange={setValue} />
        )}
        <FormFooter
          left={
            <Button variant="outlined" onClick={onRemove} disabled={working}>
              Remove
            </Button>
          }
          onCancel={onClose}
          onSubmit={async () => {
            await update.mutateAsync({ field: fieldKey, code: value });
            await apiUtils.employees.v4.fields.computed.list.refetch();
            toast.success("Field transformation updated");
            onClose();
          }}
        />
      </Stack>
    </>
  );
};

export const FieldTransformationModal = (props: TransformModalProps) => {
  const { isEnabled: aiEnabled } = useFeatureFlag(AI_TRANSFORM_BUILDER_FLAG);

  return (
    <ModalV2
      title="Edit field transformation"
      open={props.open}
      onClose={props.onClose}
      maxWidth={aiEnabled ? 1600 : 1200}
    >
      {props.open && <TransformEditor {...props} aiEnabled={aiEnabled} />}
    </ModalV2>
  );
};
